import styled from 'styled-components';
import { Link } from 'ui/links';
import { body2, caption, colors, spacing } from 'ui/theme';
import { pluralizeWord } from 'modules/campaigns/lib/utils';
import type { TopCampaign as TopCampaignType } from 'modules/missionControl/hooks/useQueryGoalTopCampaign';
import { formatAssociatedMetricValue } from './GoalCard.utils';
import { useIsOverflowing } from 'utils/hooks/useIsOverflowing';
import { TruncateTooltip } from 'ui/utility/truncate/TruncateTooltip';
import { FC, HTMLProps } from 'react';
import { GoalEventType } from 'modules/goals/types';

const TopCampaignContainer = styled.div`
  display: flex;
  padding-top: ${spacing.base};
  align-items: center;
  gap: ${spacing.base};
  border-top: 1px solid ${colors.borderPrimarySubtle};
`;

const Text = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.quarter};
`;

const Title = styled.div`
  ${caption}
  color: ${colors.textSecondary};
`;

const UnavailableText = styled.span`
  ${body2}
  color: ${colors.textDisabled};
`;

const AssociatedMetric = styled(Text)`
  flex-shrink: 0;
`;

const CampaignInfo = styled(Text)`
  flex: 1;
  box-sizing: border-box;
  overflow: hidden;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: ${colors.textPrimary};
  width: fit-content;
  max-width: 100%;
  display: inline-flex;
  box-sizing: border-box;
`;

type TopCampaignProps = {
  eventType: GoalEventType;
  topCampaign: TopCampaignType;
  associatedMetricLabel: string;
  associatedMetricValue: number;
};

export const TopCampaign = ({
  eventType,
  topCampaign,
  associatedMetricLabel,
  associatedMetricValue,
}: TopCampaignProps) => {
  const { elementRef, maxViewableWidth } = useIsOverflowing<HTMLDivElement>();

  const { count: campaignCount, bestCampaign } = topCampaign || {};
  const campaignId = bestCampaign?.campaignId;
  const campaignName = bestCampaign?.campaignName;

  const isCustomEvent = eventType === GoalEventType.CustomEvent;
  const hasAssociatedMetric =
    !!associatedMetricLabel &&
    associatedMetricValue !== undefined &&
    associatedMetricValue !== null; // NOTE: this value can be 0, which is falsy.

  if (!campaignId && !hasAssociatedMetric) {
    return null;
  }

  return (
    <TopCampaignContainer id="top-campaign">
      {/* Custom events don't have associated metrics */}
      {!isCustomEvent && hasAssociatedMetric && (
        <AssociatedMetric data-test="goal-associated-rate">
          <Title>{associatedMetricLabel}</Title>
          {formatAssociatedMetricValue(associatedMetricLabel, associatedMetricValue)}
        </AssociatedMetric>
      )}

      <CampaignInfo ref={elementRef} data-test="goal-top-campaign">
        <Title>
          Top campaign
          {!!campaignCount && (
            <>
              {' '}
              out of {campaignCount} {pluralizeWord('campaign', campaignCount)}
            </>
          )}
        </Title>

        {campaignName ? (
          <TruncateTooltip
            LinkComponent={StyledLink as FC<React.PropsWithChildren<HTMLProps<HTMLAnchorElement>>>}
            name={campaignName}
            link={`/analytics/campaignPerformance?campaignId=${campaignId}`}
            maxWidth={maxViewableWidth}
          />
        ) : (
          <UnavailableText>Unavailable</UnavailableText>
        )}
      </CampaignInfo>
    </TopCampaignContainer>
  );
};
